import {
  ButtonGroup,
  closeDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {object, SchemaOf, string} from 'yup';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {$MoneyAmount} from '@dms/teas';

import {
  type OfferAlternativeTerms,
  type OfferAlternativeUpsellLine,
} from '../types/OfferAlternative';
import {isStaleRevisionError} from '../utils/handleOfferAlternativeError';

export type AlternativeUpsellFormValues = {
  name: string;
  sellingPriceWithVat: string;
  purchasePriceWithoutVat: string | null;
};

interface AlternativeUpsellDialogProps {
  dialogId: string;
  canReadPurchasePrice: boolean;
  currency: OfferAlternativeTerms['currency'];
  line?: OfferAlternativeUpsellLine;
  onSubmit: (values: AlternativeUpsellFormValues) => Promise<unknown>;
}

export function AlternativeUpsellDialog(props: AlternativeUpsellDialogProps) {
  // A saved line whose selling price is withheld keeps it unless the user types a new one.
  const isSellingPriceRequired = isNil(props.line) || !isNil(props.line.sellingPrice);
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const currencySymbol = isCurrency(props.currency)
    ? formatCurrencySymbol(props.currency)
    : props.currency;

  const handleSubmit: FormSubmitHandler<AlternativeUpsellFormValues> = (values) =>
    props
      .onSubmit(values)
      .then(() => closeDialog(props.dialogId))
      // Already reported; a stale revision closes so reopening reads the fresh lines.
      .catch((error: unknown) => {
        if (isStaleRevisionError(error)) {
          closeDialog(props.dialogId);
        }
      });

  return (
    <Form<AlternativeUpsellFormValues>
      onSubmit={handleSubmit}
      schema={getSchema(isSellingPriceRequired)}
      defaultValues={{
        name: props.line?.name ?? '',
        sellingPriceWithVat: props.line?.sellingPrice?.withVat.amount ?? '',
        purchasePriceWithoutVat: props.line?.purchasePriceWithoutVat?.amount ?? null,
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            type="text"
            name="name"
            control={control}
            label={i18n.t('entity.person.labels.title')}
            isRequired
          />
          <FormField
            type="number"
            name="sellingPriceWithVat"
            control={control}
            label={i18n.t('entity.offerAlternative.upsell.sellingPriceWithVat', {
              defaultValue: 'Selling price incl. VAT',
            })}
            suffix={currencySymbol}
            isRequired={isSellingPriceRequired}
          />
          {/* Hidden without the purchase-price permission; the line keeps its saved value. */}
          <Show when={props.canReadPurchasePrice}>
            <FormField
              type="number"
              name="purchasePriceWithoutVat"
              control={control}
              label={i18n.t('entity.offerAlternative.upsell.purchasePriceWithoutVat', {
                defaultValue: 'Purchase price excl. VAT',
              })}
              suffix={currencySymbol}
            />
          </Show>
          <ButtonGroup align="right">
            <FormButton
              control={control}
              type="button"
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={() => closeDialog(props.dialogId)}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const SELLING_PRICE_LIMIT = {min: {amount: '0.01'}};

const getSchema = (isSellingPriceRequired: boolean): SchemaOf<AlternativeUpsellFormValues> =>
  object().shape({
    name: string().required(i18n.t('general.notifications.errorFieldRequired')),
    sellingPriceWithVat: isSellingPriceRequired
      ? $MoneyAmount(SELLING_PRICE_LIMIT).required(
          i18n.t('general.notifications.errorFieldRequired')
        )
      : $MoneyAmount(SELLING_PRICE_LIMIT),
    purchasePriceWithoutVat: $MoneyAmount().defined().nullable(),
  });
